import { useAuth } from "features/auth/hooks/useAuth";
import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { AppRoute } from "shared/types/AppRoute";

interface Props {
  route: AppRoute;
  children: ReactNode;
}

export default function ProtectedMainRoute({ route, children }: Props) {
  const {
    isAuthenticated,
    hasDomainAccess,
    hasPermission,
    getPermissionScope,
    isLoading,
  } =
    useAuth();

  if (isLoading) return null;

  if (!isAuthenticated) return <Navigate to="/auth/login" replace />;

  const hasDomain = route.domain ? hasDomainAccess(route.domain) : true;
  const hasAllRequired = route.requiredPermissions
    ? route.requiredPermissions.every((permission) => hasPermission(permission))
    : true;
  const hasAnyPermissions =
    !route.requiredPermissionsAny?.length ||
    route.requiredPermissionsAny.some((permission) => hasPermission(permission));
  const hasAnyPermissionScope =
    !route.requiredPermissionScopesAny?.length ||
    route.requiredPermissionScopesAny.some(({ permission, scopes }) => {
      const scope = getPermissionScope(permission);
      return scope != null && scopes.includes(scope);
    });

  if (!hasDomain || !hasAllRequired || !hasAnyPermissions || !hasAnyPermissionScope) {
    return <Navigate to="/not-found" replace />;
  }

  return children;
}
